import { Controller } from "@hotwired/stimulus"
import { LoopEngine } from "../loop_engine"
import { fromServerPayload } from "../loop_engine_serializer"
import { fetchLoopState, patchLoopState } from "../loop_state_client"
import { isAudioUnlocked, playCountdownBeep, playTransitionChime, warmUpMediaPlayback } from "../audio_alerts"
import { shouldPlayTransitionChime, transitionNotifyKey } from "../transition_alerts"
import { logTransition } from "../transition_debug"

const RING_CIRCUMFERENCE = 283
const MAX_TIMEOUT_MS = 2_147_483_647

export default class extends Controller {
  static targets = [
    "activeLabel",
    "activeLabelRow",
    "block",
    "blockDot",
    "blockPlay",
    "countdownPanel",
    "nextLine",
    "pauseControl",
    "pauseButton",
    "playControl",
    "playButton",
    "skipButton",
    "ringMinutes",
    "ringProgress",
    "ringSeconds",
    "ringSecondsRow",
    "resetBlockInline",
    "statusLabel",
    "transitionNextLabel",
    "transitionPanel",
    "transport"
  ]

  static values = {
    state: Object,
    autoResume: Boolean,
    workerUrl: String
  }

  connect() {
    this.engine = new LoopEngine(fromServerPayload(this.stateValue))
    this.waitingForResume = this.element.dataset.resumePromptOpenValue === "true"
    this.worker = null
    this.workerEnabled = true
    this.rafId = null
    this.transitionAlarmId = null
    this.hiddenPollId = null
    this.lastBeepSecond = null
    this.syncing = false
    this.pendingSyncCount = 0
    this.syncChain = Promise.resolve()
    this.tabWasHidden = document.hidden
    this.lastDispatchedTransitionKey = null

    this.onVisibilityChange = this.onVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.onVisibilityChange)

    this.updateUi()

    if (this.autoResumeValue) {
      this.performAutoResume()
    } else if (!this.waitingForResume && this.engine.status === "running") {
      this.startTickLoop()
    }
  }

  async performAutoResume() {
    const catchUpResult = this.engine.catchUp(Date.now())
    this.updateUi()

    if (catchUpResult.caughtUp) {
      this.handleCatchUpTransitions(catchUpResult.transitions)
    } else if (this.engine.status === "awaiting_switch") {
      this.announceAwaitingSwitch()
    }

    if (this.engine.status === "running") {
      this.startTickLoop()
    }
  }

  disconnect() {
    this.stopTickLoop()
    this.terminateWorker()
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
  }

  async play() {
    await warmUpMediaPlayback()

    const result = this.engine.play()
    if (!result.ok) return

    this.lastBeepSecond = null
    this.updateUi()
    await this.syncState()
    this.startTickLoop()
  }

  async pause() {
    this.engine.pause()
    this.stopTickLoop()
    this.lastBeepSecond = null
    this.updateUi()
    await this.syncState()
  }

  async resume() {
    await warmUpMediaPlayback()

    const result = this.engine.resume()
    if (!result.ok) return

    this.updateUi()
    await this.syncState()
    this.startTickLoop()
  }

  async skip() {
    const result = this.engine.skip()
    if (!result.ok) return

    if (result.dispatchTransition) {
      logTransition("manual skip — alerts suppressed", {
        from: result.from,
        to: result.to,
        fromLabel: result.fromLabel,
        toLabel: result.toLabel
      })
    }

    this.updateUi()
    await this.syncState()

    if (this.engine.status === "running") {
      this.startTickLoop()
    }
  }

  async playBlock(event) {
    const index = Number(event.currentTarget.dataset.blockIndex)
    if (Number.isNaN(index)) return

    if (this.engine.status === "awaiting_switch") return

    const isCurrent = index === this.engine.currentIndex

    if (isCurrent && this.engine.status === "running") return

    if (isCurrent) {
      await warmUpMediaPlayback()

      const result = this.engine.play()
      if (!result.ok) return

      this.updateUi()
      await this.syncState()
      this.startTickLoop()
      return
    }

    const result = this.engine.jumpTo(index, Date.now(), { start: true })
    if (!result.ok) return

    if (result.dispatchTransition) {
      this.dispatchTransition({
        from: result.from,
        to: result.to,
        fromLabel: result.fromLabel,
        toLabel: result.toLabel,
        toDurationMs: result.toDurationMs
      })
    }

    this.updateUi()
    await this.syncState()
    this.startTickLoop()
  }

  async acknowledgeSwitch() {
    const result = this.engine.acknowledgeSwitch()
    if (!result.ok) return

    this.updateUi()
    await this.syncState()
    this.startTickLoop()
  }

  async confirmReset() {
    if (!window.confirm("Reset to the first activity?")) return

    await this.reset()
  }

  async confirmResetBlock() {
    if (!window.confirm("Reset the current activity timer?")) return

    await this.resetCurrentBlock()
  }

  async reset() {
    this.engine.reset()
    this.stopTickLoop()
    this.lastBeepSecond = null
    this.updateUi()
    await this.syncState()
  }

  async resetCurrentBlock() {
    const result = this.engine.resetCurrentBlock()
    if (!result.ok) return

    this.lastBeepSecond = null
    this.updateUi()
    await this.syncState()

    if (this.engine.status === "running") {
      this.startTickLoop()
    } else {
      this.stopTickLoop()
    }
  }

  onResumeContinued() {
    this.waitingForResume = false

    if (this.engine.status === "paused") {
      this.resume()
    } else if (this.engine.status === "running") {
      this.startTickLoop()
      this.updateUi()
    } else if (this.engine.status === "awaiting_switch") {
      this.updateUi()
    }
  }

  async onResumeReset() {
    await this.reset()
  }

  async onVisibilityChange() {
    if (document.visibilityState === "visible") {
      const missedWhileBackground = this.tabWasHidden
      this.tabWasHidden = false

      await warmUpMediaPlayback({ allowCreate: false })
      this.clearHiddenPoll()
      await this.reconcileFromServer({ missedWhileBackground })
      return
    }

    this.tabWasHidden = true
    this.doTick(Date.now())
    this.scheduleTransitionAlarm()
    this.startHiddenPoll()
  }

  async reconcileFromServer({ missedWhileBackground = false } = {}) {
    try {
      await this.syncChain

      const payload = await fetchLoopState()
      this.engine = new LoopEngine(fromServerPayload(payload))
      const catchUpResult = this.engine.catchUp(Date.now())
      this.updateUi()

      if (catchUpResult.caughtUp) {
        this.handleCatchUpTransitions(catchUpResult.transitions, { missedWhileBackground })
      } else if (this.engine.status === "awaiting_switch") {
        this.announceAwaitingSwitch({ missedWhileBackground })
      }

      if (this.engine.status === "running") {
        this.terminateWorker()
        this.startTickLoop()
      } else {
        this.stopTickLoop()
      }

      this.doTick(Date.now())
    } catch (error) {
      console.error("Failed to reconcile loop state:", error)
    }
  }

  startTickLoop() {
    this.stopTickLoop()
    this.scheduleTransitionAlarm()

    if (!this.workerEnabled) {
      this.startRafFallback()
      return
    }

    try {
      if (!this.worker) {
        if (!this.hasWorkerUrlValue) {
          throw new Error("timer worker URL missing")
        }

        this.worker = new Worker(this.workerUrlValue)
        this.worker.onmessage = (event) => {
          if (event.data.type === "tick") {
            this.doTick(event.data.now)
          }
        }
        this.worker.onerror = () => {
          this.workerEnabled = false
          this.terminateWorker()
          this.startRafFallback()
        }
      }

      this.worker.postMessage({ type: "start" })
    } catch (_error) {
      this.workerEnabled = false
      this.terminateWorker()
      this.startRafFallback()
    }

    if (document.hidden) {
      this.startHiddenPoll()
    }
  }

  startRafFallback() {
    const tick = () => {
      this.doTick(Date.now())

      if (this.engine.status === "running") {
        this.rafId = requestAnimationFrame(tick)
      }
    }

    this.rafId = requestAnimationFrame(tick)

    if (document.hidden) {
      this.startHiddenPoll()
    }
  }

  stopTickLoop() {
    this.clearTransitionAlarm()
    this.clearHiddenPoll()

    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }

    if (this.worker) {
      this.worker.postMessage({ type: "stop" })
    }
  }

  scheduleTransitionAlarm() {
    this.clearTransitionAlarm()

    if (this.engine.status !== "running" || this.engine.targetEndTime == null) return

    const delay = Math.max(0, this.engine.targetEndTime - Date.now() + 1)
    const timeoutMs = Math.min(delay, MAX_TIMEOUT_MS)

    this.transitionAlarmId = setTimeout(() => {
      this.transitionAlarmId = null
      this.doTick(Date.now())

      if (this.engine.status === "running") {
        this.scheduleTransitionAlarm()
      }
    }, timeoutMs)

    if (this.worker) {
      this.worker.postMessage({ type: "schedule", at: this.engine.targetEndTime })
    }
  }

  clearTransitionAlarm() {
    if (this.transitionAlarmId != null) {
      clearTimeout(this.transitionAlarmId)
      this.transitionAlarmId = null
    }
  }

  startHiddenPoll() {
    this.clearHiddenPoll()

    if (this.engine.status !== "running") return

    this.hiddenPollId = setInterval(() => {
      if (!document.hidden) {
        this.clearHiddenPoll()
        return
      }

      if (this.engine.status !== "running") {
        this.clearHiddenPoll()
        return
      }

      this.doTick(Date.now())
    }, 1000)
  }

  clearHiddenPoll() {
    if (this.hiddenPollId != null) {
      clearInterval(this.hiddenPollId)
      this.hiddenPollId = null
    }
  }

  terminateWorker() {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
  }

  doTick(now, { silent = false } = {}) {
    const catchUpResult = this.engine.catchUp(now)

    if (catchUpResult.caughtUp && !silent) {
      this.lastBeepSecond = null
      this.handleCatchUpTransitions(catchUpResult.transitions)
      this.stopTickLoop()
    }

    this.updateCountdown(now)
    this.checkCountdownBeeps(now)
    this.updateBlocks()
    this.updateTransport()
  }

  checkCountdownBeeps(now) {
    if (this.engine.status !== "running") {
      this.lastBeepSecond = null
      return
    }

    const remaining = this.engine.remainingMsAt(now)
    const seconds = Math.ceil(remaining / 1000)

    if (seconds >= 1 && seconds <= 5) {
      if (seconds !== this.lastBeepSecond) {
        this.lastBeepSecond = seconds
        playCountdownBeep()
      }
    } else if (seconds > 5) {
      this.lastBeepSecond = null
    }
  }

  handleCatchUpTransitions(transitions, { missedWhileBackground = false } = {}) {
    if (transitions.length === 0) return

    this.handleTransition(transitions[transitions.length - 1], { missedWhileBackground })
  }

  dispatchTransition(detail, { missedWhileBackground = false } = {}) {
    const notifyKey = transitionNotifyKey(detail)

    if (this.lastDispatchedTransitionKey === notifyKey) {
      logTransition("position switch skipped", { detail, reason: "duplicate", notifyKey })
      return
    }

    this.lastDispatchedTransitionKey = notifyKey

    const hidden = document.hidden
    const audioUnlocked = isAudioUnlocked()
    const notificationsGranted = this.notificationsGranted()
    const playChime = shouldPlayTransitionChime({ audioUnlocked })
    const eventDetail = { ...detail, missedWhileBackground }

    logTransition("position switch", {
      detail: eventDetail,
      hidden,
      missedWhileBackground,
      audioUnlocked,
      notificationsGranted,
      playChime,
      notify: notificationsGranted,
      alertPlan: [
        notificationsGranted ? "browser notification" : null,
        audioUnlocked ? "transition chime" : null
      ]
        .filter(Boolean)
        .join(" + ") || "press Play and enable notifications"
    })

    if (playChime) {
      playTransitionChime()
    }

    document.dispatchEvent(
      new CustomEvent("timer:transition", { detail: eventDetail })
    )

    logTransition("timer:transition event dispatched", { detail: eventDetail })
  }

  notificationsGranted() {
    return "Notification" in window && Notification.permission === "granted"
  }

  announceAwaitingSwitch({ missedWhileBackground = false } = {}) {
    const fromBlock = this.engine.currentBlock()
    const nextBlock = this.engine.blocks[this.engine.nextIndex()]

    this.dispatchTransition(
      {
        from: this.engine.currentIndex,
        to: this.engine.nextIndex(),
        fromLabel: fromBlock?.label,
        toLabel: nextBlock?.label
      },
      { missedWhileBackground }
    )
  }

  async handleTransition(result, { missedWhileBackground = false } = {}) {
    this.dispatchTransition(result, { missedWhileBackground })

    if (this.syncing) return

    this.syncing = true
    try {
      await this.syncState()
    } finally {
      this.syncing = false
    }
  }

  async syncState() {
    this.setSyncPending(true)

    this.syncChain = this.syncChain.then(async () => {
      const payload = await patchLoopState(this.engine.snapshot())
      this.engine = new LoopEngine(fromServerPayload(payload))
      this.updateUi()
    }).catch((error) => {
      console.error("Loop state sync failed:", error)
    }).finally(() => {
      this.setSyncPending(false)
    })

    return this.syncChain
  }

  setSyncPending(pending) {
    this.pendingSyncCount += pending ? 1 : -1
    this.element.dataset.timerPendingSync = this.pendingSyncCount > 0 ? "true" : "false"
  }

  updateUi() {
    this.updateCountdown()
    this.updateBlocks()
    this.updateTransport()
    this.updateStatusLabel()
    this.updateTransitionPanel()
  }

  updateCountdown(now = Date.now()) {
    this.updateRingProgress(now)

    if (!this.hasRingMinutesTarget) return

    const remaining = this.engine.remainingMsAt(now)
    const totalSeconds = Math.max(0, Math.ceil(remaining / 1000))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (n) => String(n).padStart(2, "0")

    let top
    let bottom

    if (hours > 0) {
      top = `${pad(hours)}:${pad(minutes)}`
      bottom = pad(seconds)
    } else if (minutes > 0) {
      top = pad(minutes)
      bottom = pad(seconds)
    } else {
      top = String(seconds)
      bottom = ""
    }

    this.ringMinutesTarget.textContent = top
    this.ringSecondsTarget.textContent = bottom

    if (this.hasRingSecondsRowTarget) {
      this.ringSecondsRowTarget.classList.toggle("hidden", bottom === "")
    }

    if (this.hasActiveLabelTarget) {
      const block = this.engine.currentBlock()
      const show = block && this.engine.status !== "awaiting_switch"
      this.activeLabelTarget.textContent = block
        ? `${block.label} • ${this.formatDurationFull(block.durationMs)}`
        : ""
      this.activeLabelRowTarget.classList.toggle("hidden", !show)
      this.activeLabelRowTarget.classList.toggle("flex", show)
    }

    if (this.hasNextLineTarget) {
      const nextBlock = this.engine.blocks[this.engine.nextIndex()]
      const started = this.engine.status === "running" || this.engine.status === "paused"
      if (started && nextBlock && this.engine.blocks.length > 1 && this.engine.status !== "awaiting_switch") {
        this.nextLineTarget.textContent = `Next: ${nextBlock.label} (${this.formatDurationShort(nextBlock.durationMs)})`
        this.nextLineTarget.classList.remove("hidden")
      } else {
        this.nextLineTarget.textContent = ""
        this.nextLineTarget.classList.add("hidden")
      }
    }
  }

  updateRingProgress(now = Date.now()) {
    if (!this.hasRingProgressTarget) return

    const remaining = this.engine.remainingMsAt(now)
    const segmentDuration = this.blockDurationForProgress()
    const percent =
      segmentDuration > 0
        ? Math.min(100, (remaining / segmentDuration) * 100)
        : 0
    const offset = RING_CIRCUMFERENCE * (1 - percent / 100)

    this.ringProgressTarget.setAttribute("stroke-dashoffset", String(offset))
  }

  blockDurationForProgress() {
    const block = this.engine.blockDurationMs()
    const segment = Number(this.engine.segmentDurationMs)
    const remaining = Number(this.engine.remainingMsAt(Date.now()))

    // Paused should prefer server-persisted segment duration on refresh.
    if (this.engine.status === "paused") {
      if (Number.isFinite(segment) && segment > 0) {
        return segment
      }

      return block > 0 ? block : 1
    }

    // Idle has no active segment; fall back to current block length.
    if (this.engine.status === "idle") {
      if (block > 0) return block
      return remaining > 0 ? remaining : 1
    }

    if (Number.isFinite(segment) && segment > 0) {
      return segment
    }

    if (block > 0) return block
    return remaining > 0 ? remaining : 1
  }

  updateTransitionPanel() {
    const awaiting = this.engine.status === "awaiting_switch"

    if (this.hasCountdownPanelTarget) {
      this.countdownPanelTarget.classList.toggle("hidden", awaiting)
      this.countdownPanelTarget.classList.toggle("flex", !awaiting)
    }

    if (this.hasTransitionPanelTarget) {
      this.transitionPanelTarget.classList.toggle("hidden", !awaiting)
      this.transitionPanelTarget.classList.toggle("flex", awaiting)
    }

    if (this.hasTransitionNextLabelTarget && awaiting) {
      const nextBlock = this.engine.blocks[this.engine.nextIndex()]
      this.transitionNextLabelTarget.textContent = nextBlock?.label || "—"
    }
  }

  updateBlocks() {
    const running = this.engine.status === "running"
    const awaiting = this.engine.status === "awaiting_switch"

    this.blockTargets.forEach((element) => {
      const index = Number(element.dataset.blockIndex)
      const dot = element.querySelector("[data-timer-target='blockDot']")
      const playButton = element.querySelector("[data-timer-target='blockPlay']")

      element.classList.remove("border-l-4", "border-success", "opacity-60")
      dot?.classList.remove(
        "bg-success",
        "bg-base-300",
        "bg-transparent",
        "timer-block-dot-pulse",
        "h-5",
        "w-5",
        "rounded-none",
        "text-success",
        "text-base",
        "font-bold",
        "leading-none",
        "flex",
        "items-center",
        "justify-center"
      )
      if (dot) dot.textContent = ""

      if (index < this.engine.currentIndex) {
        dot?.classList.add(
          "h-5",
          "w-5",
          "rounded-none",
          "bg-transparent",
          "text-success",
          "text-base",
          "font-bold",
          "leading-none",
          "flex",
          "items-center",
          "justify-center"
        )
        if (dot) dot.textContent = "✓"
      } else if (index === this.engine.currentIndex) {
        element.classList.add("border-l-4", "border-success")
        dot?.classList.add("bg-success")
        if (running) dot?.classList.add("timer-block-dot-pulse")
      } else {
        element.classList.add("opacity-60")
        dot?.classList.add("bg-base-300")
      }

      if (playButton) {
        const isCurrentRunning =
          index === this.engine.currentIndex && running && !awaiting
        playButton.disabled = isCurrentRunning
        playButton.classList.toggle("btn-disabled", isCurrentRunning)
      }
    })
  }

  updateTransport() {
    if (!this.hasPlayButtonTarget || !this.hasPauseButtonTarget) return

    const awaiting = this.engine.status === "awaiting_switch"
    const running = this.engine.status === "running"

    if (this.hasTransportTarget) {
      this.transportTarget.classList.toggle("hidden", awaiting)
    }

    this.playControlTarget.classList.toggle("hidden", running || awaiting)
    this.pauseControlTarget.classList.toggle("hidden", !running || awaiting)
  }

  updateStatusLabel() {
    if (!this.hasStatusLabelTarget) return

    const labels = {
      idle: "Ready to Start",
      running: "Running",
      paused: "Paused",
      awaiting_switch: "Switch position"
    }

    this.statusLabelTarget.textContent = labels[this.engine.status] || "Ready to Start"
    this.statusLabelTarget.classList.toggle("hidden", this.engine.status === "awaiting_switch")

    const showInlineReset = this.engine.status === "running" || this.engine.status === "paused"

    if (this.hasResetBlockInlineTarget) {
      this.resetBlockInlineTarget.classList.toggle("hidden", !showInlineReset)
    }
  }

  formatDurationShort(durationMs) {
    return this.formatDurationFull(durationMs)
  }

  formatDurationFull(durationMs) {
    const totalSeconds = Math.max(0, Math.round(durationMs / 1000))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const parts = []
    if (hours > 0) parts.push(hours === 1 ? "1 hr" : `${hours} hrs`)
    if (minutes > 0) parts.push(minutes === 1 ? "1 min" : `${minutes} mins`)
    if (seconds > 0) parts.push(seconds === 1 ? "1 sec" : `${seconds} secs`)

    return parts.join(" ") || "0 secs"
  }
}
